import React from "react";
import {
  Paper,
  Box,
  Typography,
  Switch,
  Select,
  MenuItem,
  FormControl,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Divider,
} from "@mui/material";
import { CommunicationSettings } from "shared/types";

interface CommunicationSectionProps {
  settings: CommunicationSettings;
  onChange: (key: keyof CommunicationSettings, value: string | boolean) => void;
}

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#178B8E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#178B8E",
  },
};

const radioSx = {
  color: "#178B8E",
  "&.Mui-checked": { color: "#178B8E" },
};

const SwitchRow: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.5,
    }}
  >
    <Box>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={switchSx}
    />
  </Box>
);

const CommunicationSection: React.FC<CommunicationSectionProps> = ({
  settings,
  onChange,
}) => {
  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Communication Preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage how you communicate with sitters
      </Typography>

      {/* Preferred Contact Method */}
      <Box mb={3}>
        <Typography variant="body2" fontWeight={600} mb={1}>
          Preferred Contact Method
        </Typography>
        <RadioGroup
          value={settings.preferredMethod}
          onChange={(e) => onChange("preferredMethod", e.target.value)}
        >
          <FormControlLabel
            value="messaging"
            control={<Radio size="small" sx={radioSx} />}
            label={
              <Typography variant="body2">
                In-app messaging{" "}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  (recommended)
                </Typography>
              </Typography>
            }
          />
          <FormControlLabel
            value="email"
            control={<Radio size="small" sx={radioSx} />}
            label={<Typography variant="body2">Email</Typography>}
          />
          <FormControlLabel
            value="phone"
            control={<Radio size="small" sx={radioSx} />}
            label={<Typography variant="body2">Phone call</Typography>}
          />
          <FormControlLabel
            value="any"
            control={<Radio size="small" sx={radioSx} />}
            label={<Typography variant="body2">Any method</Typography>}
          />
        </RadioGroup>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Response Time */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={500}>
            Expected Response Time
          </Typography>
          <Typography variant="caption" color="text.secondary">
            How quickly you typically respond to messages
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={settings.responseTime}
            onChange={(e) => onChange("responseTime", e.target.value)}
          >
            <MenuItem value="1-hour">Within 1 hour</MenuItem>
            <MenuItem value="4-hours">Within 4 hours</MenuItem>
            <MenuItem value="24-hours">Within 24 hours</MenuItem>
            <MenuItem value="no-commitment">No commitment</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Switches */}
      <Stack divider={<Divider />}>
        <SwitchRow
          label="Auto-reply when unavailable"
          description="Send automatic reply to messages"
          checked={settings.autoReply}
          onChange={(v) => onChange("autoReply", v)}
        />
        {settings.autoReply && (
          <Box sx={{ py: 1.5 }}>
            <TextField
              label="Auto-reply message"
              value={settings.autoReplyMessage}
              onChange={(e) => onChange("autoReplyMessage", e.target.value)}
              multiline
              rows={2}
              fullWidth
              size="small"
              placeholder="I'm currently unavailable. I'll get back to you soon!"
            />
          </Box>
        )}
        <SwitchRow
          label="Auto-decline when unavailable"
          description="Automatically decline booking requests when busy"
          checked={settings.autoDecline}
          onChange={(v) => onChange("autoDecline", v)}
        />
        <SwitchRow
          label="Require detailed pet information"
          description="Request full pet profile before accepting bookings"
          checked={settings.requirePetInfo}
          onChange={(v) => onChange("requirePetInfo", v)}
        />
      </Stack>
    </Paper>
  );
};

export default CommunicationSection;
